/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState, useEffect } from 'react';
import { Controller, Control } from 'react-hook-form';
import Button from '@mui/material/Button';
import AttachFileIcon from '@mui/icons-material/AttachFile';

type FilePickerProps = {
  name?: string;
  control?: Control<any>;
  label?: string;
  accept?: string;
  disabled?: boolean;
  value?: string | null; // base64 string ou path
  onChange?: (fileBase64: string | null, fileObj?: File | null) => void;
  sx?: React.CSSProperties;
  color?:
    | 'primary'
    | 'secondary'
    | 'inherit'
    | 'success'
    | 'error'
    | 'info'
    | 'warning';
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

// Função para extrair o nome do arquivo de um path
function extractFileName(path: string): string {
  if (!path) return '';
  // Se for base64, retorna vazio
  if (path.startsWith('data:')) return '';
  // Extrai o nome do arquivo do path
  return path.split('/').pop() || path;
}

function formatFileName(name: string, maxLength = 16): string {
  if (!name) return '';
  if (name.length <= maxLength) return name;
  return '...' + name.slice(-maxLength);
}

export const FilePicker: React.FC<FilePickerProps> = ({
  name,
  control,
  label = 'Selecionar arquivo',
  accept,
  disabled,
  value: propValue,
  onChange: propOnChange,
  sx,
  color = 'secondary',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | undefined>(undefined);

  // Atualiza o nome do arquivo se value for um path vindo da API
  useEffect(() => {
    if (
      typeof propValue === 'string' &&
      propValue &&
      !propValue.startsWith('data:')
    ) {
      setFileName(extractFileName(propValue));
    }
  }, [propValue]);

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (base64: string | null) => void,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      const base64 = await fileToBase64(file);
      onChange(base64);
      propOnChange?.(base64, file);
    } else {
      setFileName(undefined);
      onChange(null);
      propOnChange?.(null, null);
    }
  };

  const renderButton = (
    _: string | null,
    onChange: (base64: string | null) => void,
    error?: string,
  ) => (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => handleFileChange(e, onChange)}
        disabled={disabled}
      />
      <Button
        variant="contained"
        color={color}
        startIcon={<AttachFileIcon />}
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        sx={{ textTransform: 'none', ...sx }}
        fullWidth
      >
        {fileName ? formatFileName(fileName) : label}
      </Button>
      {error && <span style={{ color: 'red', fontSize: 12 }}>{error}</span>}
    </>
  );

  if (control && name) {
    // Controlado pelo React Hook Form
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange }, fieldState }) =>
          renderButton(value, onChange, fieldState.error?.message)
        }
      />
    );
  }

  // Não controlado
  return renderButton(propValue ?? null, propOnChange ?? (() => {}));
};
